import { Clause, RegisteredArea, SecuredFlag } from './types';

/**
 * How a `SecuredConfig` is evaluated, with no React attached.
 *
 * Split out of `securedDecoration.tsx` so these rules can be used — and unit-tested — without
 * dragging in the provider, the router and the auth context behind it. `securedDecoration` re-exports
 * everything here, so existing imports keep working.
 */

export type RuleItem = SecuredFlag | Clause;
export type RuleInput = SecuredFlag | RuleItem[] | undefined;

export interface SecuredConfig {
  id: string;
  /**
   * Which one of a repeated area this is: a row id, a ramp id, a field key. The area registers as
   * `${id}-${instance}`, so ids stay unique per row; the assistant's page context groups the rows of one
   * `id` into a single entry listing the instances. Leave it out for an area that appears once.
   */
  instance?: string;
  visible?: RuleInput;
  editable?: RuleInput;
  label?: string;
  /**
   * What the section is and whose it is, for the AI assistant when the area is hidden or read-only,
   * e.g. 'Internal note of the notification owner; partners never see it.'
   */
  description?: string;
  /**
   * Non-security context shown as its own line in the inspection box — e.g. why a mass action is
   * greyed out for the current selection. Deliberately not a clause: it explains the UI state, not
   * the access decision, and never affects `visible` / `editable`.
   */
  note?: string;
}

export const isFlag = (item: RuleItem): item is SecuredFlag => 'granted' in item;
export const isSatisfied = (item: RuleItem): boolean => (isFlag(item) ? item.granted : item.satisfied);

export const normalize = (input: RuleInput): RuleItem[] => {
  if (input == null) return [];
  if (Array.isArray(input)) return input;
  return [input];
};

export const allSatisfied = (items: RuleItem[]): boolean => items.every(isSatisfied);
export const flagsOf = (items: RuleItem[]): SecuredFlag[] => items.filter(isFlag);
export const inlineClausesOf = (items: RuleItem[]): Clause[] => items.filter((i): i is Clause => !isFlag(i));

/** The id an area registers and renders under: the base id, plus the instance when there is one. */
export const securedAreaId = (config: Pick<SecuredConfig, 'id' | 'instance'>): string =>
  config.instance ? `${config.id}-${config.instance}` : config.id;

/**
 * What an area registers in the security registry: under its full id (with the instance), so every row
 * stays its own area in the security-mode overlay, plus `baseId` / `instance` so the AI assistant's page
 * context can group the rows of one area.
 */
export const registeredAreaOf = (config: SecuredConfig): RegisteredArea => {
  const visibleItems = normalize(config.visible);
  const editItems = normalize(config.editable);
  return {
    id: securedAreaId(config),
    baseId: config.id,
    instance: config.instance,
    label: config.label,
    description: config.description,
    note: config.note,
    showFlags: flagsOf(visibleItems),
    showInline: inlineClausesOf(visibleItems),
    editableFlags: flagsOf(editItems),
    editableInline: inlineClausesOf(editItems),
    visible: visibleItems.length === 0 || allSatisfied(visibleItems),
    editable: editItems.length === 0 ? null : allSatisfied(editItems),
  };
};

export const isSecuredVisible = (config?: SecuredConfig): boolean => {
  const items = normalize(config?.visible);
  return items.length === 0 || allSatisfied(items);
};
